import React from "react";
import { Icon, IconName } from "./Icon";

/** Primitive UI components ported from oab-v1-design/components/primitives.jsx.
 *  All read design tokens (var(--token)) — no literal colors. */

// ============ BUTTONS ============
type ButtonVariant = "default" | "primary" | "ghost" | "soft" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  active?: boolean;
  style?: React.CSSProperties;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "default",
  size = "md",
  icon,
  iconRight,
  children,
  active,
  style,
  ...rest
}) => {
  const h = size === "sm" ? 26 : size === "lg" ? 40 : 32;
  const px = size === "sm" ? 10 : size === "lg" ? 16 : 12;
  const fs = size === "sm" ? 12 : size === "lg" ? 14 : 13;
  const styles = {
    default: { bg: "var(--bg-surface)", fg: "var(--fg-primary)", bd: "var(--border-default)" },
    primary: { bg: "var(--accent)", fg: "var(--fg-on-accent)", bd: "var(--accent)" },
    ghost: { bg: "transparent", fg: "var(--fg-secondary)", bd: "transparent" },
    soft: { bg: "var(--bg-surface-2)", fg: "var(--fg-primary)", bd: "transparent" },
    danger: { bg: "var(--bg-surface)", fg: "var(--status-err)", bd: "var(--border-default)" },
  }[variant];
  return (
    <button
      type="button"
      {...rest}
      data-active={active || undefined}
      style={{
        height: h,
        padding: `0 ${px}px`,
        borderRadius: "var(--r-md)",
        background: active ? "var(--accent-soft)" : styles.bg,
        color: active ? "var(--accent)" : styles.fg,
        border: `1px solid ${active ? "var(--accent-border)" : styles.bd}`,
        fontSize: fs,
        fontWeight: 500,
        letterSpacing: "-0.005em",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        transition: "background 120ms, border-color 120ms, color 120ms",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={size === "sm" ? 13 : 15} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === "sm" ? 13 : 15} />}
    </button>
  );
};

interface IconButtonProps {
  icon: IconName;
  size?: number;
  active?: boolean;
  title?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  style?: React.CSSProperties;
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  size = 32,
  active,
  title,
  onClick,
  style,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    data-active={active || undefined}
    style={{
      width: size,
      height: size,
      borderRadius: "var(--r-md)",
      background: active ? "var(--accent-soft)" : "transparent",
      color: active ? "var(--accent)" : "var(--fg-secondary)",
      border: active ? "1px solid var(--accent-border)" : "1px solid transparent",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background 120ms",
      ...style,
    }}
  >
    <Icon name={icon} size={size > 30 ? 16 : 14} />
  </button>
);

// ============ BADGES & PILLS ============
type BadgeTone = "neutral" | "accent" | "ok" | "warn" | "err";

interface BadgeProps {
  tone?: BadgeTone;
  children?: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
}

export const Badge: React.FC<BadgeProps> = ({ tone = "neutral", children, mono, style }) => {
  const tones: Record<BadgeTone, { bg: string; fg: string; bd: string }> = {
    neutral: { bg: "var(--bg-surface-2)", fg: "var(--fg-secondary)", bd: "var(--border-subtle)" },
    accent: { bg: "var(--accent-soft)", fg: "var(--accent)", bd: "var(--accent-border)" },
    ok: { bg: "var(--status-ok-bg)", fg: "var(--status-ok)", bd: "transparent" },
    warn: { bg: "var(--status-warn-bg)", fg: "var(--status-warn)", bd: "transparent" },
    err: { bg: "var(--status-err-bg)", fg: "var(--status-err)", bd: "transparent" },
  };
  const t = tones[tone];
  return (
    <span
      className={mono ? "mono tab-num" : "tab-num"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 7px",
        borderRadius: 999,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: mono ? "0.02em" : "-0.005em",
        lineHeight: 1.5,
        ...style,
      }}
    >
      {children}
    </span>
  );
};

// Status dot with optional count
type StatusKind = "ok" | "warn" | "err" | "stale" | "info";

interface StatusDotProps {
  kind?: StatusKind;
  count?: number;
  style?: React.CSSProperties;
}

export const StatusDot: React.FC<StatusDotProps> = ({ kind = "stale", count, style }) => {
  const map: Record<StatusKind, { color: string; glyph: string }> = {
    ok: { color: "var(--status-ok)", glyph: "●" },
    warn: { color: "var(--status-warn)", glyph: "●" },
    err: { color: "var(--status-err)", glyph: "●" },
    stale: { color: "var(--status-stale)", glyph: "○" },
    info: { color: "var(--status-info)", glyph: "●" },
  };
  const m = map[kind];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        color: m.color,
        fontSize: 11,
        fontWeight: 600,
        ...style,
      }}
    >
      <span style={{ fontSize: 10, lineHeight: 1 }}>{m.glyph}</span>
      {count != null && <span className="mono tab-num">{count}</span>}
    </span>
  );
};

// Module dot — small filled circle for module type
type ModuleKind = "light" | "mark" | "fence" | "drain" | "sign" | "wall" | "rail" | "signal";

interface ModuleDotProps {
  module: ModuleKind | string;
  count?: number;
  size?: number;
  showCount?: boolean;
}

export const ModuleDot: React.FC<ModuleDotProps> = ({
  module,
  count,
  size = 7,
  showCount = false,
}) => {
  const colorMap: Record<string, string> = {
    light: "var(--mod-light)",
    mark: "var(--mod-mark)",
    fence: "var(--mod-fence)",
    drain: "var(--mod-drain)",
    sign: "var(--mod-sign)",
    wall: "var(--mod-wall)",
    rail: "var(--mod-rail)",
    signal: "var(--mod-signal)",
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: colorMap[module] || "var(--fg-muted)",
          flexShrink: 0,
        }}
      />
      {showCount && (
        <span className="mono tab-num" style={{ fontSize: 11, color: "var(--fg-secondary)" }}>
          {count}
        </span>
      )}
    </span>
  );
};

// KBD key
interface KbdProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export const Kbd: React.FC<KbdProps> = ({ children, style }) => (
  <span
    className="mono"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      background: "var(--bg-surface-2)",
      border: "1px solid var(--border-subtle)",
      borderBottomWidth: 2,
      borderRadius: 4,
      fontSize: 10.5,
      color: "var(--fg-secondary)",
      fontWeight: 500,
      ...style,
    }}
  >
    {children}
  </span>
);

// Section label — small uppercase header used liberally
interface SectionLabelProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  action?: React.ReactNode;
}

export const SectionLabel: React.FC<SectionLabelProps> = ({ children, style, action }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: "0.08em",
      color: "var(--fg-tertiary)",
      textTransform: "uppercase",
      fontFamily: "var(--font-mono)",
      ...style,
    }}
  >
    <span>{children}</span>
    {action}
  </div>
);
